// ==============================================
// Admin Fleets (Organizations) Page
// ==============================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usePageSize } from '../../hooks/usePageSize';
import { PageSizeSelect } from '../../components/ui/PageSizeSelect';
import {
  MagnifyingGlassIcon,
  BuildingOffice2Icon,
  PlusIcon,
  EnvelopeIcon,
  CalendarDaysIcon,
  DevicePhoneMobileIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';

export function AdminFleetsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pageSize, setPageSize] = usePageSize('fleets');
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, pageSize]);

  // Fetch organizations
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'organizations', { search: debouncedSearch, page, pageSize }],
    queryFn: () =>
      api.adminListOrganizations({
        search: debouncedSearch || undefined,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      }),
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Fleet Management</h1>
          <p className="text-slate-400">Manage organizations and their device fleets</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          Create Organization
        </button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search organizations by name..."
            className="w-full pl-10 pr-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:border-cyan-500 focus:outline-none"
          />
        </div>
      </div>

      {/* Organization Cards */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : !data?.organizations?.length ? (
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-16 text-center">
          <BuildingOffice2Icon className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-lg text-slate-400">No organizations yet</p>
          <p className="text-sm text-slate-500 mt-1">
            Create your first organization to start managing device fleets.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
          >
            <PlusIcon className="w-5 h-5" />
            Create Organization
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.organizations.map((org: any) => (
            <button
              key={org.id}
              onClick={() => navigate(`/admin/fleets/${org.id}`)}
              className="bg-slate-800/50 rounded-xl border border-slate-700 p-5 text-left hover:border-cyan-500/50 hover:bg-slate-800/70 transition-all group"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                    <BuildingOffice2Icon className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white group-hover:text-cyan-400 transition-colors">
                      {org.name}
                    </h3>
                    <p className="text-xs text-slate-500 font-mono">{org.slug}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2 mt-4">
                {/* Contact Email */}
                <div className="flex items-center gap-2 text-sm">
                  <EnvelopeIcon className="w-4 h-4 text-slate-500 flex-shrink-0" />
                  <span className="text-slate-400 truncate">
                    {org.contact_email || 'No email set'}
                  </span>
                </div>

                {/* Device Count */}
                <div className="flex items-center gap-2 text-sm">
                  <DevicePhoneMobileIcon className="w-4 h-4 text-slate-500 flex-shrink-0" />
                  <span className="text-slate-400">
                    {org.device_count !== undefined && org.device_count !== null
                      ? `${org.device_count} device${org.device_count !== 1 ? 's' : ''}`
                      : '\u2014'}
                  </span>
                </div>

                {/* Created Date */}
                <div className="flex items-center gap-2 text-sm">
                  <CalendarDaysIcon className="w-4 h-4 text-slate-500 flex-shrink-0" />
                  <span className="text-slate-400">
                    {org.created_at
                      ? new Date(org.created_at).toLocaleDateString('en-NZ')
                      : '\u2014'}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Pagination + page size */}
      {data && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <PageSizeSelect value={pageSize} onChange={setPageSize} />
            <p className="text-sm text-slate-400">
              {data.total === 0
                ? 'No organizations'
                : `Showing ${(page - 1) * pageSize + 1} to ${Math.min(page * pageSize, data.total)} of ${data.total} organizations`}
            </p>
          </div>
          {data.total > pageSize && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="p-2 text-slate-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeftIcon className="w-5 h-5" />
              </button>
              <span className="text-sm text-white">
                Page {page} of {Math.ceil(data.total / pageSize)}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= Math.ceil(data.total / pageSize)}
                className="p-2 text-slate-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRightIcon className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Create Organization Modal */}
      {showCreateModal && (
        <CreateOrganizationModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] });
            setShowCreateModal(false);
          }}
        />
      )}
    </div>
  );
}

// ==========================================
// Create Organization Modal
// ==========================================

function CreateOrganizationModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    contact_email: '',
    notes: '',
  });
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) =>
      api.adminCreateOrganization({
        name: data.name,
        slug: data.slug,
        contact_email: data.contact_email || undefined,
        notes: data.notes || undefined,
      }),
    onSuccess: () => {
      onCreated();
    },
  });

  // Auto-generate slug from name
  const generateSlug = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleNameChange = (name: string) => {
    setFormData((prev) => ({
      ...prev,
      name,
      slug: slugManuallyEdited ? prev.slug : generateSlug(name),
    }));
  };

  const handleSlugChange = (slug: string) => {
    setSlugManuallyEdited(true);
    setFormData((prev) => ({ ...prev, slug }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 w-full max-w-md">
        <h2 className="text-xl font-bold text-white mb-4">Create Organization</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
              required
              placeholder="Acme Fishing Co."
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:border-cyan-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">
              Slug *
            </label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              required
              placeholder="acme-fishing-co"
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white font-mono text-sm placeholder-slate-400 focus:border-cyan-500 focus:outline-none"
            />
            <p className="text-xs text-slate-500 mt-1">
              Auto-generated from name. Used as a unique identifier.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">
              Contact Email
            </label>
            <input
              type="email"
              value={formData.contact_email}
              onChange={(e) =>
                setFormData({ ...formData, contact_email: e.target.value })
              }
              placeholder="admin@acmefishing.com"
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:border-cyan-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              rows={3}
              placeholder="Optional notes about this organization..."
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:border-cyan-500 focus:outline-none resize-none"
            />
          </div>

          {createMutation.isError && (
            <p className="text-red-400 text-sm">
              {(createMutation.error as any)?.response?.data?.error ||
                'Failed to create organization'}
            </p>
          )}

          <div className="flex gap-3 justify-end pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || !formData.name || !formData.slug}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Organization'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdminFleetsPage;
